import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getGroups } from '../../services/firestoreService';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('Groups component mounted, calling loadGroups...');
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading groups from Firebase...');
      const groupsData = await getGroups(20); // Load up to 20 groups
      console.log('Groups loaded successfully:', groupsData);
      console.log('Number of groups:', groupsData.length);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
      setError(`Failed to load groups: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for groups (fallback - remove this later)
  const mockGroups = [
    {
      id: 1,
      name: 'Investment Banking Professionals',
      description: 'A group for investment banking professionals to discuss deals, market trends, and career development.',
      members: 156,
      posts: 324,
      category: 'Industry',
      image: '/images/groups/investment-banking.jpg'
    },
    {
      id: 2,
      name: 'Private Equity Network',
      description: 'Connect with other private equity professionals to discuss investment strategies, deal sourcing, and portfolio management.',
      members: 98,
      posts: 215,
      category: 'Industry',
      image: '/images/groups/private-equity.jpg'
    },
    {
      id: 3,
      name: 'M&A Deal Discussion',
      description: 'A forum to discuss recent M&A transactions, valuation methodologies, and deal structures.',
      members: 142,
      posts: 287,
      category: 'Topic',
      image: '/images/groups/ma-deals.jpg'
    },
    {
      id: 4,
      name: 'New York City Finance Professionals',
      description: 'Local group for finance professionals in the New York City area to network and discuss regional opportunities.',
      members: 203,
      posts: 412,
      category: 'Location',
      image: '/images/groups/nyc.jpg'
    },
    {
      id: 5,
      name: 'Women in Finance',
      description: 'A supportive community for women in finance to connect, share experiences, and discuss career advancement.',
      members: 87,
      posts: 176,
      category: 'Interest',
      image: '/images/groups/women-finance.jpg'
    },
    {
      id: 6,
      name: 'Technology M&A',
      description: 'Focused on mergers and acquisitions in the technology sector, including valuation, due diligence, and integration.',
      members: 112,
      posts: 243,
      category: 'Topic',
      image: '/images/groups/tech-ma.jpg'
    },
    {
      id: 7,
      name: 'Early Career Finance Professionals',
      description: 'A group for analysts and associates to network, share resources, and discuss career development.',
      members: 175,
      posts: 329,
      category: 'Interest',
      image: '/images/groups/early-career.jpg'
    },
    {
      id: 8,
      name: 'Healthcare Finance',
      description: 'Discussions focused on financial aspects of the healthcare industry, including M&A, valuation, and regulatory considerations.',
      members: 94,
      posts: 187,
      category: 'Industry',
      image: '/images/groups/healthcare.jpg'
    }
  ];

  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('members'); // 'members', 'posts', 'name'
  const [userGroups, setUserGroups] = useState([1, 3, 5]); // Mock user's joined groups
  const [currentPage, setCurrentPage] = useState(1);
  const [groupsPerPage] = useState(6); // Number of groups to display per page

  // Use real groups data or fallback to mock data
  const displayGroups = groups.length > 0 ? groups : mockGroups;

  // Debug logging
  console.log('Groups state:', groups);
  console.log('Mock groups:', mockGroups);
  console.log('Display groups:', displayGroups);
  console.log('Using real data?', groups.length > 0);

  // Get unique categories for filter dropdown
  const categories = [...new Set(displayGroups.map(group => group.category))];

  // Filter groups based on search and category
  const filteredGroups = displayGroups.filter(group => {
    const matchesSearch =
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === '' || group.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  // Sort groups based on selected sort option
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    if (sortBy === 'members') {
      return (b.memberCount || b.members || 0) - (a.memberCount || a.members || 0);
    } else if (sortBy === 'posts') {
      return (b.posts || 0) - (a.posts || 0);
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  // We're using the userGroups state variable defined above

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue mx-auto mb-4"></div>
          <p>Loading groups from Firebase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Error Loading Groups</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={loadGroups}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Groups</h1>
          </div>
        </div>
      </div>

      {/* Groups Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* My Groups Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">My Groups</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups
                .filter(group => userGroups.includes(group.id))
                .map(group => (
                  <div key={group.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                    <div className="h-40 bg-white relative overflow-hidden">
                      <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                        <img
                          src={`${process.env.PUBLIC_URL}${group.image}`}
                          alt={group.name}
                          className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                          }}
                        />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2">{group.name}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">{group.description}</p>
                      <div className="flex justify-between text-sm text-gray-500 mb-4">
                        <span>{group.memberCount || group.members || 0} members</span>
                        <span>{group.posts || 0} posts</span>
                      </div>
                      <Link
                        to={`/groups/${group.id}`}
                        className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                      >
                        View Group
                      </Link>
                    </div>
                  </div>
                ))}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-dashed border-gray-300 flex items-center justify-center p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Create a New Group</h3>
                  <p className="text-gray-500 mb-4">Start a discussion on a topic you're passionate about</p>
                  <Link
                    to="/groups/create"
                    className="inline-block bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                  >
                    Create Group
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">Discover Groups</h2>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  placeholder="Search groups by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div className="flex space-x-4 w-full md:w-auto">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Categories</option>
                  {categories.map((category, index) => (
                    <option key={index} value={category}>{category}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="members">Sort by Members</option>
                  <option value="posts">Sort by Activity</option>
                  <option value="name">Sort by Name</option>
                </select>
              </div>
            </div>
          </div>

          {/* All Groups */}
          {sortedGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedGroups
                .slice((currentPage - 1) * groupsPerPage, currentPage * groupsPerPage)
                .map(group => (
                <div key={group.id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                  <Link to={`/groups/${group.id}`} className="block h-40 bg-white relative overflow-hidden">
                    <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                      <img
                        src={`${process.env.PUBLIC_URL}${group.image}`}
                        alt={group.name}
                        className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                        }}
                      />
                    </div>
                    <div className="absolute top-0 right-0 bg-gray-100 text-gray-700 px-3 py-1 text-sm">
                      {group.category}
                    </div>
                  </Link>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{group.name}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{group.description}</p>
                    <div className="flex justify-between text-sm text-gray-500 mb-4">
                      <span>{group.memberCount || group.members || 0} members</span>
                      <span>{group.posts || 0} posts</span>
                    </div>
                    {userGroups.includes(group.id) ? (
                      <Link
                        to={`/groups/${group.id}`}
                        className="block w-full bg-apn-blue text-white text-center py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                      >
                        View Group
                      </Link>
                    ) : (
                      <button
                        onClick={() => setUserGroups([...userGroups, group.id])}
                        className="block w-full bg-gray-800 text-white text-center py-2 px-4 rounded hover:bg-gray-700 transition duration-200"
                      >
                        Join Group
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No groups found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {sortedGroups.length > groupsPerPage && (
            <div className="mt-12 flex justify-center">
              <nav className="inline-flex rounded-md shadow">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-l-md border border-gray-300 ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>

                {/* Generate page buttons */}
                {Array.from({ length: Math.ceil(sortedGroups.length / groupsPerPage) }, (_, i) => i + 1)
                  .map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 border-t border-b border-gray-300 ${currentPage === pageNum ? 'bg-apn-blue text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {pageNum}
                    </button>
                  ))}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(sortedGroups.length / groupsPerPage)))}
                  disabled={currentPage === Math.ceil(sortedGroups.length / groupsPerPage)}
                  className={`px-4 py-2 rounded-r-md border border-gray-300 ${currentPage === Math.ceil(sortedGroups.length / groupsPerPage) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>



      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Start Your Own Group</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Have a topic you're passionate about? Create a group and connect with like-minded professionals.
          </p>
          <Link to="/groups/create" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Create a Group
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Groups;
